/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';
import { useAlert } from 'dashboard/composables';

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => callback(registration))
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  if (auth.hasAuthCookie()) {
    return NotificationSubscriptions.create(
      getPushSubscriptionPayload(subscription)
    );
  }
  return null;
};

export const registerSubscription = (onSuccess = () => {}) => {
  if (!window.chatwootConfig.vapidPublicKey) {
    return;
  }
  navigator.serviceWorker.ready
    .then(serviceWorkerRegistration =>
      serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: window.chatwootConfig.vapidPublicKey,
      })
    )
    .then(sendRegistrationToServer)
    .then(() => {
      onSuccess();
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('Push subscription registration failed:', error);
      useAlert('This browser does not support desktop notification');
    });
};

export const requestPushPermissions = ({ onSuccess = () => {} } = {}) => {
  if (!('Notification' in window)) {
    console.warn('Notification is not supported');
    useAlert('This browser does not support desktop notification');
  } else if (Notification.permission === 'granted') {
    if (window.chatwootConfig && window.chatwootConfig.vapidPublicKey) {
      registerSubscription(onSuccess);
    } else {
      onSuccess();
    }
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission(permission => {
      if (permission === 'granted') {
        if (window.chatwootConfig && window.chatwootConfig.vapidPublicKey) {
          registerSubscription(onSuccess);
        } else {
          onSuccess();
        }
      }
    });
  }
};

export const showDesktopNotification = ({
  title = 'Chatwoot',
  body = '',
  icon = '/favicon.png',
  tag = '',
  onClick = null,
} = {}) => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    console.warn(
      '[Notification] Notification API is not supported in this browser'
    );
    return null;
  }
  if (Notification.permission !== 'granted') {
    console.warn(
      '[Notification] Current permission is not granted:',
      Notification.permission
    );
    return null;
  }

  const options = {
    body,
    icon: icon || '/favicon.png',
  };
  if (tag) options.tag = tag;

  console.info('[Notification] Triggering native Notification:', title, body);

  try {
    const notification = new Notification(title, options);
    notification.onclick = event => {
      event.preventDefault();
      try {
        window.focus();
      } catch (e) {
        // ignore
      }
      if (typeof onClick === 'function') {
        onClick();
      }
      notification.close();
    };

    setTimeout(() => {
      try {
        notification.close();
      } catch (e) {
        // ignore
      }
    }, 8000);

    return notification;
  } catch (error) {
    console.warn(
      '[Notification] Native Notification constructor failed, fallback to ServiceWorker:',
      error
    );
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready
        .then(reg => {
          reg.showNotification(title, options);
        })
        .catch(e => {
          console.error('[Notification] SW fallback error:', e);
        });
    }
    return null;
  }
};

/* eslint-disable no-alert */
if (typeof window !== 'undefined') {
  window.testDesktopNotification = async () => {
    if (!('Notification' in window)) {
      alert('当前浏览器不支持 Notification API');
      return;
    }
    if (Notification.permission !== 'granted') {
      const res = await Notification.requestPermission();
      if (res !== 'granted') {
        alert(
          '通知权限未允许，当前状态: ' +
            res +
            '。请检查浏览器设置与 macOS 系统设置！'
        );
        return;
      }
    }
    showDesktopNotification({
      title: '🎉 桌面通知测试成功',
      body: '当收到客户新消息时，系统将在此处即时弹出横幅卡片！',
    });
  };
}
/* eslint-enable no-alert */
